import React, { useState } from "react";
import axios from "axios";

const DownloadVideos = ({ backEndUrl }) => {
    const [folderName, setFolderName] = useState("");
    const [status, setStatus] = useState("Enter a folder name and click download.");
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false); // Track bulk download progress

    const handleDownload = async () => {
        if (!folderName.trim()) {
            setStatus("Please enter a folder name.");
            return;
        }

        setLoading(true);
        setStatus("Fetching videos...");

        try {
            const response = await axios.get(`${backEndUrl}/get-videos`, {
                params: { folderName },
                headers: { "Content-Type": "application/json" },
            });

            if (response.status === 200) {
                const formattedVideos = (response.data.videos || []).map((video) => ({
                    id: video.Id,
                    name: video.Name || "Untitled Video",
                    downloadUrl: video.Urls?.DownloadUrl || null,
                    thumbnailUrl: video.Urls?.ThumbnailUrl || null,
                }));

                setVideos(formattedVideos);
                setStatus(formattedVideos.length > 0 ? "Download ready." : "No videos found.");
            } else {
                setStatus(response.data.error || "Failed to fetch videos.");
            }
        } catch (error) {
            setStatus("Error fetching videos.");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const downloadVideo = (videoUrl, videoName) => {
        if (!videoUrl) {
            alert("Download link not available.");
            return;
        }
        const link = document.createElement("a");
        link.href = videoUrl;
        link.download = `${videoName}.mp4`; // Ensures the file is named properly
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadAllVideos = async () => {
        if (videos.length === 0) {
            alert("No videos available for download.");
            return;
        }

        setDownloading(true);
        setStatus("Downloading all videos...");

        for (const video of videos) {
            if (video.downloadUrl) {
                downloadVideo(video.downloadUrl, video.name);
                await new Promise((resolve) => setTimeout(resolve, 1000)); // Delay to prevent browser blocking
            }
        }

        setDownloading(false);
        setStatus("All videos downloaded.");
    };

    return (
        <div className="p-6 max-w-lg mx-auto">
            <h1 className="text-2xl font-bold mb-4">Download Videos</h1>

            <div className="w-full mb-4">
                <input
                    type="text"
                    placeholder="Enter Folder Name"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                />
            </div>

            <div className="mb-4 flex gap-2">
                <button
                    onClick={handleDownload}
                    className={`px-4 py-2 text-white rounded ${loading ? "bg-gray-400" : "bg-blue-500 hover:bg-blue-600"}`}
                    disabled={loading}
                >
                    {loading ? "Fetching..." : "Fetch Videos"}
                </button>
                &nbsp;&nbsp;
                {videos.length > 0 && (
                    <button
                        onClick={downloadAllVideos}
                        className={`px-4 py-2 text-white rounded ${downloading ? "bg-gray-400" : "bg-green-500 hover:bg-green-600"}`}
                        disabled={downloading}
                    >
                        {downloading ? "Downloading..." : "Download All"}
                    </button>
                )}
            </div>

            <p className="mb-2">{status}</p>

            {videos.length > 0 && (
                <div className="mt-4">
                    <h2 className="text-lg font-semibold">Available Videos:</h2>
                    <ul className="mt-2 grid grid-cols-1 gap-4">
                        {videos.map((video) => (
                            <li key={video.id} className="flex flex-col items-center border-b pb-4">
                                <div className="flex justify-between w-full items-center">
                                    <span className="font-semibold">{video.name}</span>
                                    &nbsp;&nbsp;
                                    {video.downloadUrl ? (
                                        <button
                                            onClick={() => downloadVideo(video.downloadUrl, video.name)}
                                            className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                                        >
                                            Download
                                        </button>
                                    ) : (
                                        <span className="text-gray-500">No Download Link</span>
                                    )}
                                </div>
                                {video.thumbnailUrl && (
                                    <img src={video.thumbnailUrl} alt={video.name} className="w-32 h-32 rounded mt-2" />
                                )}
                                <p>&nbsp;</p>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default DownloadVideos;
